import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Select, Spin } from "antd";
import moment from "moment";
import {
  AssignTaskApi,
  GetEmployeeList,
  GetServiceTaskApi,
  GetTaskWiseFileApi,
} from "../../api/request/service";
import MySegmented from "../../components/segmented/Segmented";
import SkeletonTable from "../../components/table/SkeletonTable";
import columns from "../../columns/files/viewFile";
import View from "../../assets/View";
import Spinner from "../../components/loader/Spinner";

const ViewFile = () => {
  const { fileTypeId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [taskList, setTaskList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [segmentOptions, setSegmentOptions] = useState([]);
  const [segmentedValue, setSegmentedValue] = useState(state?.task || "");
  const [fileList, setFileList] = useState([]);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const getServiceTaskList = async () => {
    try {
      setIsLoading(true);
      const params = { service_type_id: fileTypeId };
      const { data } = await GetServiceTaskApi(params);
      if (data.status && data?.data?.length) {
        setTaskList(data.data);
        const options = data.data.map((item) => {
          return {
            label: `${item.name} (${item?.total_file_count})`,
            value: item?.name,
          };
        });
        setSegmentOptions(options);
        if (!segmentedValue) setSegmentedValue(options[0]?.value);
      }
      setIsLoading(false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setIsLoading(false);
    }
  };

  const getEmployeeData = () => {
    const task = taskList?.find((task) => task?.name === segmentedValue);
    const employeeData = employeeList
      .filter((item) =>
        item?.permissions?.some((item) => item?.id === task?.id)
      )
      .map((item) => {
        return { label: item?.name, value: item?.id };
      });
    return employeeData;
  };

  const getFileList = async (page = 1) => {
    try {
      setIsFileLoading(true);
      const task = taskList?.find((item) => item?.name === segmentedValue);
      let params = { task_id: task?.id, page: page };
      const { data } = await GetTaskWiseFileApi(params);
      const employeeData = getEmployeeData();
      if (data?.status) {
        setFileList(
          data?.data?.map((item) => {
            const assignTo = employeeData?.find(
              (emp) => emp?.value === item?.employee_id
            );
            return {
              ...item,
              name: item?.user?.name,
              email: item?.user?.email,
              file: (
                <div className="font-bold highlight-text capitalize">
                  {item?.service_name_slug}
                </div>
              ),
              submit: item?.is_submit ? "Yes" : "No",
              assignTo: (
                <Select
                  placeholder="Select employee"
                  style={{ width: "100%" }}
                  value={assignTo?.value || item?.employee_name}
                  options={employeeData}
                  onChange={(value) =>
                    assignHandler(item?.id, value, item?.employee_id)
                  }
                />
              ),
              startDate: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "",
              status: (
                <p className="text-green-500 font-bold uppercase">
                  {item?.status}
                </p>
              ),
              action: (
                <div
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(`/file/${fileTypeId}/${item?.id}`, {
                      state: { client: item?.user, task: segmentedValue },
                    })
                  }
                >
                  <View />
                </div>
              ),
            };
          })
        );
        setTotal(data?.total_records);
        setCurrentPage(page);
      }
      setIsFileLoading(false);
    } catch (error) {
      setFileList([]);
      setIsFileLoading(false);
    }
  };

  const assignHandler = async (id, employee_id, currentEmployeeId) => {
    if (employee_id === currentEmployeeId) return;
    try {
      setFileList((prev) => {
        return prev.map((item) =>
          item.id === id ? { ...item, assignTo: <Spinner /> } : item
        );
      });

      let params = { file_id: id, employee_id: employee_id };
      const { data } = await AssignTaskApi(params);
      if (data?.status) setAssignData(id, employee_id);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setAssignData(id, currentEmployeeId);
    }
  };

  const getEmployeeList = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetEmployeeList();
      if (data.status) {
        setEmployeeList(data?.data);
        columns.map((item) => {
          if (item.dataIndex === "assignTo") {
            item.filters = data?.data?.map((item) => {
              return { text: item?.name, value: item?.id };
            });
          }
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getEmployeeList();
  }, []);

  useEffect(() => {
    if (fileTypeId) getServiceTaskList();
  }, [fileTypeId]);

  useEffect(() => {
    if (taskList?.length) getFileList();
  }, [segmentedValue, taskList, employeeList]);

  const setAssignData = (id, employeeId) => {
    const employeeData = getEmployeeData();
    setFileList((prev) => {
      return prev.map((item) =>
        item.id === id
          ? {
              ...item,
              employee_id: employeeId,
              assignTo: (
                <Select
                  placeholder="Select employee"
                  style={{ width: "100%" }}
                  value={employeeId}
                  options={employeeData}
                  onChange={(value) =>
                    assignHandler(item?.id, value, employeeId)
                  }
                />
              ),
            }
          : item
      );
    });
  };

  const handleTableChange = (pagination) => {
    getFileList(pagination?.current);
  };

  return (
    <div className="border rounded-md bg-white">
      {isLoading ? (
        <div className="flex flex-col items-center gap-2 my-16">
          <Spin />
          <p className="text-secondaryLight1">Loading...</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {taskList?.length ? (
            <>
              <MySegmented
                options={segmentOptions}
                value={segmentedValue}
                setValue={setSegmentedValue}
              />

              <div className="table-radius-0 text-light mb-4">
                <SkeletonTable
                  columns={columns}
                  data={fileList}
                  isLoading={isFileLoading}
                  searchingFields={["name", "email", "file_number"]}
                  pagination={{
                    current: currentPage,
                    total: total,
                    pageSize: 10,
                    showSizeChanger: false,
                  }}
                  handleTableChange={handleTableChange}
                />
              </div>
            </>
          ) : (
            <p className="text-center text-Gray my-16">No task available</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewFile;
